/** Saved, source-linked phrase results shared by the merchant report and operator export. */
import * as z from 'zod';
import { savedRespondentProfileSchema } from './response-source-profile.js';
export { savedRespondentProfileSchema } from './response-source-profile.js';
export const RESPONSE_PHRASE_ASSESSMENT_POLICY = 'owned-quotes.v1';
const sourceOrigin = {
    origin: z.enum(['original', 'follow_up']).optional(),
    source: z.object({ key: z.string(), hash: z.string() }).optional(),
};
export const responsePhraseEvidenceSchema = z.object({
    ...sourceOrigin,
    evidenceId: z.string(),
    answerRef: z.string(),
    armId: z.string(),
    armLabel: z.string(),
    respondentLabel: z.string(),
    question: z.string(),
    response: z.string(),
    quote: z.string(),
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    catalogueEntryId: z.string().nullable(),
});
export const responsePhraseDisplayAnswerSchema = z.object({
    answerRef: z.string(),
    armLabel: z.string(),
    respondentLabel: z.string(),
    question: z.string(),
    segments: z.array(z.object({ text: z.string(), highlighted: z.boolean() })),
});
const displaySchema = z.object({
    notices: z.array(z.string()).optional(),
    ideas: z.array(z.object({
        id: z.string(),
        number: z.number().int().positive(),
        title: z.string(),
        proposal: z.string(),
        change: z.string(),
        heldFixed: z.string(),
        unresolved: z.boolean(),
        answer: responsePhraseDisplayAnswerSchema,
        supportingAnswers: z.array(responsePhraseDisplayAnswerSchema),
    })),
    otherAnswers: z.array(responsePhraseDisplayAnswerSchema),
});
export const responsePhraseInterpretationSchema = z.object({
    citationUnit: z.literal('exchange').optional(),
    /** Saved review concerns remain unresolved when final consolidation is unavailable. */
    reviewIssues: z
        .array(z.object({
        id: z.string(),
        observationIds: z.array(z.string()),
        explanation: z.string(),
        citations: z
            .array(z.object({
            exchangeRef: z.string(),
            role: z.enum(['supporting', 'qualifying']),
            passages: z.array(responsePhraseEvidenceSchema).min(1),
        }))
            .min(1),
    }))
        .optional(),
    /** Absent in saved v1-v4 history; an empty current list means no supported action was proposed. */
    actions: z
        .array(z.object({
        id: z.string(),
        kind: z.enum(['review_information', 'investigate_preference']),
        suggestion: z.string(),
        reason: z.string(),
        observationIds: z.array(z.string()),
        citationUnit: z.literal('exchange').optional(),
        supportingExchangeRefs: z.array(z.string()).optional(),
        qualifyingExchangeRefs: z.array(z.string()).optional(),
        supportingEvidenceIds: z.array(z.string()),
        qualifyingEvidenceIds: z.array(z.string()),
        supportingAnswers: z.array(responsePhraseDisplayAnswerSchema),
        qualifyingAnswers: z.array(responsePhraseDisplayAnswerSchema),
    }))
        .optional(),
    testGroups: z
        .array(z.object({
        kind: z.enum(['existing', 'future']),
        proposalIds: z.array(z.string()),
    }))
        .optional(),
    scope: z.array(z.object({
        armId: z.string(),
        armLabel: z.string(),
        selectedExchanges: z.number(),
        processedExchanges: z.number(),
        selectedAnswers: z.number(),
        processedAnswers: z.number(),
        totalAnswers: z.number(),
        questionIds: z.array(z.string()),
    })),
    observations: z.array(z.object({
        id: z.string(),
        title: z.string(),
        interpretation: z.string(),
        batch: z.number(),
        citationUnit: z.literal('exchange').optional(),
        supportingExchangeRefs: z.array(z.string()).optional(),
        qualifyingExchangeRefs: z.array(z.string()).optional(),
        supportingEvidenceIds: z.array(z.string()),
        qualifyingEvidenceIds: z.array(z.string()),
        answer: responsePhraseDisplayAnswerSchema.optional(),
        supportingAnswers: z.array(responsePhraseDisplayAnswerSchema),
        qualifyingAnswers: z.array(responsePhraseDisplayAnswerSchema),
    })),
    openQuestions: z.array(z.object({
        id: z.string(),
        question: z.string(),
        missingDistinction: z.string(),
        observationIds: z.array(z.string()),
    })),
    proposedTests: z.array(z.object({
        id: z.string(),
        questionId: z.string(),
        hypothesis: z.string(),
        changedFactor: z.string(),
        alternatives: z.array(z.string()),
        heldFixed: z.array(z.string()),
        citationUnit: z.literal('exchange').optional(),
        evidenceExchangeRefs: z.array(z.string()).optional(),
        evidenceIds: z.array(z.string()),
        status: z.enum(['already_tested', 'needs_definition', 'invalid']),
        reason: z.string(),
    })),
});
export const responsePhraseResultsSchema = z.object({
    analysisId: z.string(),
    /** Set only by the hash-verified explicit publication reader, never by provider output. */
    draftSource: z
        .strictObject({
        source: z.literal('response_phrase_synthesis'),
        studyId: z.string().min(1),
        runId: z.string().min(1),
        synthesisId: z.string().regex(/^sha256:[a-f0-9]{64}$/),
        sourceHash: z.string().regex(/^sha256:[a-f0-9]{64}$/),
    })
        .optional(),
    status: z.enum(['complete', 'partial', 'failed', 'running', 'pending', 'stale']),
    coverage: z.object({
        selectedAnswers: z.number(),
        processedAnswers: z.number(),
        totalAnswers: z.number(),
        completedBatches: z.number(),
        totalBatches: z.number(),
        failedBatches: z.number(),
        uncertainBatches: z.number(),
    }),
    assessment: z
        .object({
        policy: z.literal(RESPONSE_PHRASE_ASSESSMENT_POLICY),
        returnedAnswers: z.number(),
        partialAnswers: z.number(),
        rejectedQuotes: z.number(),
        withheldObservations: z.number(),
        withheldQuestions: z.number(),
        withheldTests: z.number(),
    })
        .optional(),
    /** Original selected input, not model output or a claim that every answer was interpreted. */
    sourceExchanges: z
        .array(z.object({
        exchangeRef: z.string().optional(),
        armId: z.string(),
        respondentId: z.string(),
        sourceChildRunId: z.string(),
        sourceHash: z.string(),
        profile: savedRespondentProfileSchema.nullable(),
        answers: z
            .array(z.object({
            answerRef: z.string(),
            questionId: z.string(),
            question: z.string(),
            response: z.string(),
            ...sourceOrigin,
        }))
            .min(1),
    }))
        .optional(),
    evidence: z.array(responsePhraseEvidenceSchema),
    /** Exact saved passages and literal wording counts. Counts do not establish agreement. */
    passageCategories: z
        .array(z.object({
        observationId: z.string(),
        passages: z.array(z.object({
            role: z.enum(['supporting', 'qualifying']),
            evidence: responsePhraseEvidenceSchema,
            counts: z.array(z.object({
                armId: z.string(),
                armLabel: z.string(),
                questionId: z.string(),
                matchingAnswers: z.number().int().nonnegative(),
                matchingRespondents: z.number().int().nonnegative(),
                totalAnswers: z.number().int().nonnegative(),
                totalRespondents: z.number().int().nonnegative(),
            })),
        })),
    }))
        .optional(),
    ideas: z.array(z.object({
        id: z.string(),
        hypotheses: z.array(z.string()),
        changeDescriptions: z.array(z.string()),
        fixedDescriptions: z.array(z.string()),
        evidenceIds: z.array(z.string()),
        resolvedComparison: z.boolean(),
    })),
    display: displaySchema,
    interpretation: responsePhraseInterpretationSchema.optional(),
});
//# sourceMappingURL=response-phrases.js.map